import { useEffect, useRef, useState, useCallback } from 'react';
import { acquireSessionChannel, type SessionChannelHandle } from '../network/sessionChannelHub';
import type { ChatMessage, ChatMessageEvent, ChatHistoryEvent } from '../types/game';

export interface ChatState {
  messages: ChatMessage[];
  unreadCount: number;
  sendMessage: (body: string) => void;
  clearUnread: () => void;
  isConnected: boolean;
}

export function useChatState(sessionId: number, chatIsActive: boolean): ChatState {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  const chatIsActiveRef = useRef(chatIsActive);
  useEffect(() => {
    chatIsActiveRef.current = chatIsActive;
  }, [chatIsActive]);

  const handleRef = useRef<SessionChannelHandle | null>(null);

  const clearUnread = useCallback(() => setUnreadCount(0), []);

  const sendMessage = useCallback((body: string) => {
    const trimmed = body.trim();
    handleRef.current?.sendMessage(trimmed);
  }, []);

  useEffect(() => {
    // No authoritative session id yet (e.g. deep link still resolving) — do not
    // open a doomed SessionChannel(0). Re-runs when the resolved id arrives.
    if (!sessionId || sessionId <= 0) {
      setIsConnected(false);
      return;
    }

    // Acquire the shared, single per-session channel (F7) rather than opening a
    // second SessionChannel for the same session.
    const handle = acquireSessionChannel(sessionId);
    handleRef.current = handle;

    handle
      .onChatHistory((event: ChatHistoryEvent) => {
        setMessages(event.messages);
        setIsConnected(true);
      })
      .onChatMessage((event: ChatMessageEvent) => {
        const msg: ChatMessage = {
          id:             event.id,
          session_id:     event.session_id,
          user_id:        event.user_id,
          sender_display: event.sender_display,
          body:           event.body,
          kind:           event.kind,
          sent_at:        event.sent_at,
        };
        setMessages(prev => [...prev, msg]);
        if (!chatIsActiveRef.current) {
          setUnreadCount(prev => prev + 1);
        }
      })
      .onReconnect(() => {
        // server re-sends chat_history on subscribed
      });

    return () => {
      handle.release();
      handleRef.current = null;
      setIsConnected(false);
    };
  // sessionId is the only stable dep; chatIsActive is tracked via ref
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return { messages, unreadCount, sendMessage, clearUnread, isConnected };
}
